import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage } from "@/lib/api";
import { CheckCircle2, Loader2, Mail, Phone, Clock, ShieldCheck } from "lucide-react";

const REQUEST_TYPES = [
  { value: "opt_out", label: "Opt out of sale/sharing of my personal information" },
  { value: "delete", label: "Delete my personal data / account" },
  { value: "access", label: "Access / receive a copy of my personal data" },
  { value: "correct", label: "Correct inaccurate personal data" },
];

export default function PrivacyRequestPage() {
  const [form, setForm] = useState({ name: "", email: "", requestType: "", details: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.requestType) {
      toast.error("Select the type of privacy request.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/privacy/requests", form);
      setSubmitted(true);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Your request could not be submitted. Please try again."), { duration: 9000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12 space-y-8">
          <div className="space-y-3">
            <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2.5" data-testid="privacy-request-title">
              <ShieldCheck className="h-7 w-7 text-primary" aria-hidden="true" /> Privacy Request
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
              Use this form to exercise your privacy rights — opting out of the sale or sharing of your
              personal information, requesting deletion, access, or correction of your data. We respond to
              verified requests within the timeframe required by applicable law. See our{" "}
              <Link to="/legal/privacy-policy" className="text-primary underline underline-offset-2">Privacy Policy</Link>{" "}
              and <Link to="/legal/privacy-choices" className="text-primary underline underline-offset-2">Privacy Choices</Link>.
            </p>
          </div>

          {submitted ? (
            <Card className="bg-card border-border" data-testid="privacy-request-success">
              <CardContent className="py-10 flex flex-col items-center text-center gap-3">
                <CheckCircle2 className="h-10 w-10 text-primary" aria-hidden="true" />
                <p className="font-display text-xl font-semibold">Request received</p>
                <p className="text-sm text-muted-foreground max-w-md">
                  Thank you. Your privacy request was submitted and our team was notified. We will respond to
                  the email address you provided.
                </p>
                <Button asChild variant="outline" size="sm" className="mt-2" data-testid="privacy-request-home-button">
                  <Link to="/">Back to home</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border">
              <CardHeader className="pb-4">
                <CardTitle className="font-display text-lg">Submit a request</CardTitle>
                <CardDescription>
                  You do not need an account to submit a request. We may contact you to verify your identity
                  before fulfilling it, as required by law.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-5" data-testid="privacy-request-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="privacy-name">Full name</Label>
                      <Input
                        id="privacy-name"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={setField("name")}
                        placeholder="Your full legal name"
                        data-testid="privacy-name-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="privacy-email">Email address</Label>
                      <Input
                        id="privacy-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={setField("email")}
                        placeholder="you@example.com"
                        data-testid="privacy-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Request type</Label>
                    <Select
                      value={form.requestType}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, requestType: value }))}
                    >
                      <SelectTrigger data-testid="privacy-type-select">
                        <SelectValue placeholder="Choose what you would like us to do" />
                      </SelectTrigger>
                      <SelectContent>
                        {REQUEST_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value} data-testid={`privacy-type-${type.value}`}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="privacy-details">Details (optional)</Label>
                    <Textarea
                      id="privacy-details"
                      rows={4}
                      maxLength={3000}
                      value={form.details}
                      onChange={setField("details")}
                      placeholder="Anything that helps us locate your information (e.g., the email used on your account)."
                      data-testid="privacy-details-input"
                    />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full sm:w-auto" data-testid="privacy-submit-button">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                    Submit privacy request
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          <Card className="bg-card border-border">
            <CardContent className="py-5 grid gap-3 sm:grid-cols-3 text-sm" data-testid="privacy-contact-block">
              <p className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
                <a href="mailto:getyourqsip@gmail.com" className="hover:text-foreground transition-colors" data-testid="privacy-contact-email">
                  getyourqsip@gmail.com
                </a>
              </p>
              <p className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-4 w-4 text-primary" aria-hidden="true" />
                <a href="tel:+12398887959" className="hover:text-foreground transition-colors" data-testid="privacy-contact-phone">
                  +1 (239) 888-7959
                </a>
              </p>
              <p className="flex items-center gap-2 text-muted-foreground" data-testid="privacy-contact-hours">
                <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
                9:00 A.M.–5:00 P.M. EST
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
